import { useEffect, useCallback, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useOnboardingStore } from '../../stores/onboarding.store';
import { useAuthStore } from '../../stores/auth.store';
import type {
  OnboardingStepId,
  GiraffeHintStage,
  NeedsGuesserHintStage,
} from '../../types/onboarding';
import { useSubscriptionStore } from '../../stores/subscription.store';
import { useCelebrationStore } from '../../stores/celebration.store';
import { useTrialModalStore } from '../../stores/trial-modal.store';
import {
  QUICK_TOUR_CONTENT,
  QUICK_TOUR_SELECTORS,
  EMPATHY_TOUR_STEPS,
} from '../../config/onboarding-steps';
import SpotlightOverlay from './SpotlightOverlay';
import OnboardingSpeechBubble from './OnboardingSpeechBubble';
import { EMPY_LAUNCHER, shellBubbleAnchor } from './bubbleAnchor';
import GoalChips from './GoalChips';
import GuidedToolHints from './GuidedToolHints';
import { useIsDesktop } from '../../hooks/useMediaQuery';

export default function OnboardingOverlay() {
  const location = useLocation();
  const navigate = useNavigate();
  const isDesktop = useIsDesktop();

  // Onboarding store
  const isActive = useOnboardingStore((s) => s.isActive);
  const currentStep = useOnboardingStore((s) => s.currentStep);
  const selectedGoal = useOnboardingStore((s) => s.selectedGoal);
  const mappedTool = useOnboardingStore((s) => s.mappedTool);
  const quickTourIndex = useOnboardingStore((s) => s.quickTourIndex);
  const quickTourPhase = useOnboardingStore((s) => s.quickTourPhase);

  // Empathy tour state
  const empathyTourActive = useOnboardingStore((s) => s.empathyTourActive);
  const empathyTourIndex = useOnboardingStore((s) => s.empathyTourIndex);
  const advanceEmpathyTour = useOnboardingStore((s) => s.advanceEmpathyTour);
  const skipEmpathyTour = useOnboardingStore((s) => s.skipEmpathyTour);

  const startOnboarding = useOnboardingStore((s) => s.startOnboarding);
  const skipOnboarding = useOnboardingStore((s) => s.skipOnboarding);
  const resumeOnboarding = useOnboardingStore((s) => s.resumeOnboarding);
  const shouldShowOnboarding = useOnboardingStore((s) => s.shouldShowOnboarding);
  const goToStep = useOnboardingStore((s) => s.goToStep);
  const selectGoal = useOnboardingStore((s) => s.selectGoal);
  const acceptVulnerability = useOnboardingStore((s) => s.acceptVulnerability);
  const declineVulnerability = useOnboardingStore((s) => s.declineVulnerability);
  const completeGuidedTool = useOnboardingStore((s) => s.completeGuidedTool);
  const advanceQuickTour = useOnboardingStore((s) => s.advanceQuickTour);
  const getCurrentQuickTourSection = useOnboardingStore((s) => s.getCurrentQuickTourSection);
  const getQuickTourSections = useOnboardingStore((s) => s.getQuickTourSections);
  const getVulnerabilityResponse = useOnboardingStore((s) => s.getVulnerabilityResponse);

  // External state
  const subscription = useSubscriptionStore((s) => s.subscription);
  const celebrationIsOpen = useCelebrationStore((s) => s.isOpen);
  const trialModalActive = useTrialModalStore((s) => s.activeModal);

  const [skipConfirmVisible, setSkipConfirmVisible] = useState(false);

  // SUBSCRIBED, not read at call time. An earlier revision used
  // useAuthStore.getState() inside the effect with a comment claiming the gate's
  // state change "remounts the tree anyway" — it does not. This overlay is a
  // root-level SIBLING of ProtectedRoute, so swapping the gate for its outlet
  // never remounts it, and with no subscription and no dependency the effect
  // would not re-run when verification cleared. A new user who verified would
  // then get no onboarding at all until a full reload (Codex #762).
  const verificationPending = useAuthStore(
    (s) => s.user?.verification_required === true && s.user?.email_verified === false,
  );

  // === Startup: Determine if onboarding should show ===
  useEffect(() => {
    if (isActive) return;

    const shouldShow = shouldShowOnboarding(
      subscription?.platform,
      celebrationIsOpen,
      trialModalActive !== null,
      verificationPending,
    );

    if (shouldShow) {
      resumeOnboarding();
      const id = setTimeout(() => {
        const store = useOnboardingStore.getState();
        if (!store.isActive && shouldShow) {
          startOnboarding();
        }
      }, 100);
      return () => clearTimeout(id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscription?.platform, celebrationIsOpen, trialModalActive, isActive, verificationPending]);

  // === Console helpers for testing & debugging (dev only) ===
  useEffect(() => {
    if (!import.meta.env.DEV) return;

    // Dev-only debug helpers — declared on `window` via a typed interface
    // below so we don't fall back to `any` for the global slots.
    interface OnboardingDebugWindow {
      startOnboarding?: () => void;
      skipOnboarding?: () => void;
      goToStep?: (stepId: string) => void;
      setHintStage?: (stage: string) => void;
      onboardingState?: () => ReturnType<typeof useOnboardingStore.getState>;
      startEmpathyTour?: () => void;
    }
    const w = window as Window & OnboardingDebugWindow;
    w.startOnboarding = () => useOnboardingStore.getState().startOnboarding();
    w.skipOnboarding = () => useOnboardingStore.getState().skipOnboarding();
    w.goToStep = (stepId: string) =>
      useOnboardingStore.getState().goToStep(stepId as OnboardingStepId);
    w.setHintStage = (stage: string) =>
      useOnboardingStore
        .getState()
        .setToolHintStage(stage as GiraffeHintStage | NeedsGuesserHintStage);
    w.onboardingState = () => useOnboardingStore.getState();
    w.startEmpathyTour = () => useOnboardingStore.getState().startEmpathyTour();

    return () => {
      delete w.startOnboarding;
      delete w.skipOnboarding;
      delete w.goToStep;
      delete w.setHintStage;
      delete w.onboardingState;
      delete w.startEmpathyTour;
    };
  }, []);

  // === Navigate during quick-tour phases ===
  useEffect(() => {
    if (!isActive || currentStep !== 'quick-tour') return;
    const phase = useOnboardingStore.getState().quickTourPhase;
    const section = useOnboardingStore.getState().getCurrentQuickTourSection();

    // Profile intro: scroll to top so the nav profile button is visible.
    // Delay slightly so the Course page content has settled after navigation.
    if (phase === 'intro' && section === 'profile') {
      const id = setTimeout(() => window.scrollTo({ top: 0, behavior: 'instant' }), 150);
      return () => clearTimeout(id);
    }

    // Empathy detail: navigate programmatically since user can't click the locked tab
    if (phase === 'detail' && section === 'empathy') {
      void navigate('/app/empathy');
    }
  }, [isActive, currentStep, quickTourIndex, quickTourPhase, navigate]);

  // === Navigate during empathy tour ===
  useEffect(() => {
    if (!empathyTourActive) return;
    // Step 0 (E button spotlight): ensure we're on the dashboard so the nav bar is visible
    if (
      empathyTourIndex === 0 &&
      !location.pathname.startsWith('/app/empathy') &&
      location.pathname !== '/app'
    ) {
      void navigate('/app');
    }
    // Steps 1+ are on the empathy page — navigate there if not already
    if (empathyTourIndex >= 1 && location.pathname !== '/app/empathy') {
      void navigate('/app/empathy');
    }
  }, [empathyTourActive, empathyTourIndex, location.pathname, navigate]);

  // === Coordinate scroll + spotlight for empathy tour ===
  // The spotlight selector is held in state so we can delay revealing it until
  // after scrolling finishes. This prevents the spotlight from jumping to the
  // element's pre-scroll position and then bouncing back.
  const [empathySpotlightSelector, setEmpathySpotlightSelector] = useState<string | undefined>();

  useEffect(() => {
    if (!empathyTourActive) {
      setEmpathySpotlightSelector(undefined);
      return;
    }

    const step = EMPATHY_TOUR_STEPS[empathyTourIndex];
    if (!step?.selector) return;

    // Step 0 (E button): no scrolling needed, show immediately
    // On desktop, the mobile E button doesn't exist — spotlight the nav-empathy button instead
    if (empathyTourIndex === 0) {
      setEmpathySpotlightSelector(isDesktop ? '[data-onboarding="nav-empathy"]' : step.selector);
      return;
    }

    // Clear spotlight while we scroll — screen stays dimmed, no cutout jump
    setEmpathySpotlightSelector(undefined);

    // Small delay for DOM to settle after navigation
    const timer = setTimeout(() => {
      const el = document.querySelector(step.selector);
      if (!el) {
        // Element not in DOM yet — just set the selector and let SpotlightOverlay poll
        setEmpathySpotlightSelector(step.selector);
        return;
      }

      // Calculate target viewport Y: center the element in the space between header and speech bubble
      const bubbleBottomOffset = isDesktop ? 116 : 196;
      const estimatedBubbleHeight = 200;
      const buffer = 32;
      const bubbleTop = window.innerHeight - bubbleBottomOffset - estimatedBubbleHeight;
      const headerHeight = 80;
      const availableSpace = bubbleTop - headerHeight;
      const elHeight = el.getBoundingClientRect().height;
      const targetViewportY = Math.max(
        headerHeight + buffer,
        headerHeight + (availableSpace - elHeight) / 2,
      );

      const elPageTop = el.getBoundingClientRect().top + window.scrollY;
      const scrollTarget = Math.max(0, elPageTop - targetViewportY);
      const needsScroll = Math.abs(scrollTarget - window.scrollY) > 5;

      if (needsScroll) {
        window.scrollTo({ top: scrollTarget, behavior: 'smooth' });
        // Reveal spotlight after scroll animation completes
        setTimeout(() => setEmpathySpotlightSelector(step.selector), 450);
      } else {
        // Already in position, show immediately
        setEmpathySpotlightSelector(step.selector);
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [empathyTourActive, empathyTourIndex, isDesktop]);

  // === Skip handlers ===
  const handleSkipClick = useCallback(() => {
    setSkipConfirmVisible(true);
  }, []);

  const handleSkipConfirm = useCallback(() => {
    setSkipConfirmVisible(false);
    skipOnboarding();
  }, [skipOnboarding]);

  const handleSkipCancel = useCallback(() => {
    setSkipConfirmVisible(false);
  }, []);

  // === Step handlers ===
  const handleWelcomeLetsGo = useCallback(() => {
    goToStep('goal-selection');
  }, [goToStep]);

  const handleWelcomeNoThanks = useCallback(() => {
    skipOnboarding();
  }, [skipOnboarding]);

  const handleGoalContinue = useCallback(() => {
    goToStep('warm-response');
  }, [goToStep]);

  const handleWarmResponseNext = useCallback(() => {
    goToStep('vulnerability');
  }, [goToStep]);

  const handleVulnerabilityYes = useCallback(() => {
    const { mappedTool } = useOnboardingStore.getState();
    const toolRoute =
      mappedTool === 'giraffe-translator'
        ? '/app/tools/giraffe-translator'
        : '/app/tools/needs-guesser';
    acceptVulnerability();
    void navigate(toolRoute);
  }, [acceptVulnerability, navigate]);

  const handleVulnerabilityMaybeLater = useCallback(() => {
    declineVulnerability();
  }, [declineVulnerability]);

  const handleContinueTour = useCallback(() => {
    completeGuidedTool();
    // Don't navigate — the nav bar is visible from any page, and navigating
    // to /app (Course) causes layout shifts that push content down.
  }, [completeGuidedTool]);

  // Current empathy tour step
  const empathyStep = empathyTourActive ? EMPATHY_TOUR_STEPS[empathyTourIndex] : null;
  const isLastEmpathyStep = empathyTourIndex === EMPATHY_TOUR_STEPS.length - 1;

  // Render empathy tour (independent of main onboarding — can run after celebrations)
  const empathyTourOverlay = empathyTourActive && empathyStep && (
    <>
      <SpotlightOverlay targetSelector={empathySpotlightSelector} disableAutoScroll />
      <OnboardingSpeechBubble visible key={`empathy-tour-${empathyTourIndex}`}>
        <p className="text-sm text-(--ee-tw-gray-800) leading-relaxed mb-3">
          {isDesktop ? empathyStep.text.replace(/\bTap\b/g, 'Click') : empathyStep.text}
        </p>
        {empathyStep.action === 'click' && (
          <>
            <p className="text-center text-xs text-(--ee-tw-gray-400) mt-1">
              {isDesktop ? 'Click' : 'Tap'} the highlighted element
            </p>
            {isLastEmpathyStep ? (
              <button
                onClick={skipEmpathyTour}
                className="mt-2 w-full py-2 rounded-xl text-sm text-(--ee-tw-gray-500) hover:bg-(--ee-tw-gray-100-bg) transition-colors"
              >
                Skip
              </button>
            ) : null}
          </>
        )}
        {empathyStep.action === 'next' && (
          <button
            onClick={advanceEmpathyTour}
            className="w-full py-2.5 rounded-xl bg-primary text-(--ee-on-accent) text-sm font-medium hover:bg-primary/90 transition-colors"
          >
            Next
          </button>
        )}
        <p className="text-center text-xs text-(--ee-tw-gray-400) mt-2">
          {empathyTourIndex + 1} of {EMPATHY_TOUR_STEPS.length}
        </p>
      </OnboardingSpeechBubble>
    </>
  );

  // If empathy tour is active but main onboarding is not, render only empathy tour
  if (!isActive && empathyTourActive) {
    return <>{empathyTourOverlay}</>;
  }

  // Don't render if not active (and no empathy tour)
  if (!isActive) return null;

  // Quick tour state
  const tourSection = getCurrentQuickTourSection();
  const tourSections = getQuickTourSections();
  const isLastTourStep = currentStep === 'quick-tour' && quickTourIndex >= tourSections.length - 1;

  // Get spotlight selector for quick tour
  const tourSelector = tourSection
    ? isDesktop
      ? QUICK_TOUR_SELECTORS[tourSection].desktop
      : QUICK_TOUR_SELECTORS[tourSection].mobile
    : undefined;

  // Conversational steps that all spotlight Empy
  const isConversationalStep = [
    'welcome',
    'goal-selection',
    'warm-response',
    'vulnerability',
  ].includes(currentStep);

  // The conversational steps talk from Empy: their bubble points at the
  // launcher, which is docked in the compact header below 1280.
  const empyAnchor = shellBubbleAnchor(EMPY_LAUNCHER, isDesktop);

  return (
    <>
      {/* Persistent Empy spotlight across all conversational steps — no flicker between transitions */}
      {isConversationalStep ? (
        <SpotlightOverlay targetSelector={EMPY_LAUNCHER} borderRadius={9999} padding={4} />
      ) : null}

      {/* ====== WELCOME ====== */}
      {currentStep === 'welcome' && (
        <OnboardingSpeechBubble visible anchor={empyAnchor} target={EMPY_LAUNCHER}>
          <p className="text-sm text-(--ee-tw-gray-800) leading-relaxed mb-4">
            Hey! I&apos;m Empy, your friendly giraffe here to help and support you. I&apos;d love to
            chat with you briefly and show you around.
          </p>
          <div className="flex gap-2">
            <button
              onClick={handleWelcomeNoThanks}
              className="flex-1 py-2 rounded-xl text-sm text-(--ee-tw-gray-500) hover:bg-(--ee-tw-gray-100-bg) transition-colors"
            >
              Skip Tour
            </button>
            <button
              onClick={handleWelcomeLetsGo}
              className="flex-1 py-2 rounded-xl bg-primary text-(--ee-on-accent) text-sm font-medium hover:bg-primary/90 transition-colors"
            >
              Let&apos;s Go!
            </button>
          </div>
        </OnboardingSpeechBubble>
      )}

      {/* ====== GOAL SELECTION ====== */}
      {currentStep === 'goal-selection' && (
        <OnboardingSpeechBubble
          visible
          maxWidth={isDesktop ? 420 : 340}
          onSkip={handleSkipClick}
          anchor={empyAnchor}
          target={EMPY_LAUNCHER}
        >
          <p className="text-sm text-(--ee-tw-gray-800) leading-relaxed mb-3">
            So, I&apos;m super excited about you being here! Here are a few things you can achieve
            through this app — what resonates the most?
          </p>
          <GoalChips
            selectedGoal={selectedGoal}
            onSelect={selectGoal}
            onContinue={handleGoalContinue}
          />
        </OnboardingSpeechBubble>
      )}

      {/* ====== WARM RESPONSE ====== */}
      {currentStep === 'warm-response' && (
        <OnboardingSpeechBubble
          visible
          onSkip={handleSkipClick}
          anchor={empyAnchor}
          target={EMPY_LAUNCHER}
        >
          <p className="text-sm text-(--ee-tw-gray-800) leading-relaxed mb-4">
            {getVulnerabilityResponse()}
          </p>
          <button
            onClick={handleWarmResponseNext}
            className="w-full py-2.5 rounded-xl bg-primary text-(--ee-on-accent) text-sm font-medium hover:bg-primary/90 transition-colors"
          >
            Next
          </button>
        </OnboardingSpeechBubble>
      )}

      {/* ====== VULNERABILITY ====== */}
      {currentStep === 'vulnerability' && (
        <OnboardingSpeechBubble
          visible
          onSkip={handleSkipClick}
          anchor={empyAnchor}
          target={EMPY_LAUNCHER}
        >
          <p className="text-sm text-(--ee-tw-gray-800) leading-relaxed mb-4">
            There&apos;s something I&apos;d like to show you. Are you willing to be vulnerable for a
            minute?
          </p>
          <div className="flex gap-2">
            <button
              onClick={handleVulnerabilityMaybeLater}
              className="flex-1 py-2 rounded-xl text-sm text-(--ee-tw-gray-500) hover:bg-(--ee-tw-gray-100-bg) transition-colors"
            >
              Maybe Later
            </button>
            <button
              onClick={handleVulnerabilityYes}
              className="flex-1 py-2 rounded-xl bg-primary text-(--ee-on-accent) text-sm font-medium hover:bg-primary/90 transition-colors"
            >
              Yes
            </button>
          </div>
        </OnboardingSpeechBubble>
      )}

      {/* ====== GUIDED TOOL ====== */}
      {currentStep === 'guided-tool' && mappedTool ? (
        <GuidedToolHints tool={mappedTool} onContinueTour={handleContinueTour} />
      ) : null}

      {/* ====== QUICK TOUR ====== */}
      {currentStep === 'quick-tour' && tourSection ? (
        <>
          {/* Intro phase: spotlight the nav element */}
          {quickTourPhase === 'intro' &&
            (() => {
              // Anchor the bubble near the highlighted element: desktop top
              // bar items under the bar, desktop launchers above them; below
              // 1280 the docked launchers and avatar under the header target,
              // the bottom nav's items above the bottom nav (./bubbleAnchor).
              const introAnchor = shellBubbleAnchor(tourSelector, isDesktop);
              return (
                <>
                  <SpotlightOverlay targetSelector={tourSelector} />
                  <OnboardingSpeechBubble
                    visible
                    onSkip={!isLastTourStep ? handleSkipClick : undefined}
                    anchor={introAnchor}
                    target={tourSelector}
                    key={`tour-intro-${quickTourIndex}`}
                  >
                    <p className="text-sm text-(--ee-tw-gray-800) leading-relaxed mb-3">
                      {QUICK_TOUR_CONTENT[tourSection].intro}
                    </p>
                    {/* Empathy section: show Next button (can't click locked tab) */}
                    {tourSection === 'empathy' && (
                      <button
                        onClick={advanceQuickTour}
                        className="w-full py-2.5 rounded-xl bg-primary text-(--ee-on-accent) text-sm font-medium hover:bg-primary/90 transition-colors"
                      >
                        Next
                      </button>
                    )}
                    {tourSection !== 'empathy' && (
                      <p className="text-center text-xs text-(--ee-tw-gray-400) mt-1">
                        {isDesktop ? 'Click' : 'Tap'} the highlighted element to continue
                      </p>
                    )}
                    <p className="text-center text-xs text-(--ee-tw-gray-400) mt-2">
                      {quickTourIndex + 1} of {tourSections.length}
                    </p>
                  </OnboardingSpeechBubble>
                </>
              );
            })()}

          {/* Detail phase: on the destination page, show detail text */}
          {quickTourPhase === 'detail' && (
            <OnboardingSpeechBubble
              visible
              onSkip={!isLastTourStep ? handleSkipClick : undefined}
              key={`tour-detail-${quickTourIndex}`}
            >
              <p className="text-sm text-(--ee-tw-gray-800) leading-relaxed mb-3">
                {QUICK_TOUR_CONTENT[tourSection].detail}
              </p>
              <button
                onClick={advanceQuickTour}
                className="w-full py-2.5 rounded-xl bg-primary text-(--ee-on-accent) text-sm font-medium hover:bg-primary/90 transition-colors"
              >
                {isLastTourStep ? 'Get Started' : 'Next'}
              </button>
              <p className="text-center text-xs text-(--ee-tw-gray-400) mt-2">
                {quickTourIndex + 1} of {tourSections.length}
              </p>
            </OnboardingSpeechBubble>
          )}
        </>
      ) : null}

      {/* ====== EMPATHY TOUR (can co-exist with main onboarding, but typically runs standalone) ====== */}
      {empathyTourOverlay}

      {/* ====== SKIP CONFIRMATION ====== */}
      {skipConfirmVisible ? (
        <div className="fixed inset-0 z-[300] flex items-center justify-center bg-(--ee-scrim)/50 p-4">
          <div className="bg-(--ee-surface) rounded-2xl p-6 max-w-xs w-full shadow-xl">
            <h3 className="font-semibold text-(--ee-tw-gray-900) mb-2">Skip the tour?</h3>
            <p className="text-sm text-(--ee-tw-gray-600) mb-4">
              You can always explore these features on your own. The tour won&apos;t show again.
            </p>
            <div className="flex gap-3">
              <button onClick={handleSkipCancel} className="btn btn-ghost flex-1">
                Stay
              </button>
              <button onClick={handleSkipConfirm} className="btn btn-primary flex-1">
                Skip
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
